<template>
    <div>
        <button
            @click="goToPreviousMonth"
            class="nav-btn top-left icon-circle-left"
        ></button>
        <button
            @click="goToNextMonth"
            class="nav-btn top-right icon-circle-right"
        ></button>
        <button v-if="isMobile" class="close-btn" @click="applyDates">
            ×
        </button>
        <div v-if="isMobile" class="close-btn-divider"></div>
        <div class="header">
            <div class="months">
                <div
                    class="month"
                    v-for="(offset, index) in isMobile ? [0] : [0, 1]"
                    :key="index"
                >
                    <h3>{{ getMonthYearUTC(currentUtcDate, offset) }}</h3>
                    <div class="days">
                        <div
                            class="day-header"
                            v-for="dayName in weekdayNames"
                            :key="dayName"
                        >
                            {{ dayName }}
                        </div>

                        <div
                            class="day-cell empty"
                            v-for="n in getFirstDayOffset(
                                currentUtcDate,
                                offset
                            )"
                            :key="'empty-' + n"
                        ></div>

                        <div
                            class="day-cell"
                            v-for="(day, dayIndex) in getDaysInMonthUTC(
                                currentUtcDate,
                                offset
                            )"
                            :key="day.iso"
                            :class="{
                                selected: isSelectedDay(day.iso),
                                'in-between':
                                    isInBetween(day.iso) ||
                                    isInBetweenWithHover(day.iso),
                                disabled: isBeforeMinDate(day.iso)
                            }"
                            @mouseenter="hoveredDate = day.iso"
                            @mouseleave="hoveredDate = null"
                        >
                            <button
                                class="day"
                                @click="selectDay(day.iso)"
                                :disabled="isBeforeMinDate(day.iso)"
                            >
                                {{ day.day }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="button-row">
            <button
                class="small-btn"
                :class="{ selected: selectedRange === 0 }"
                @click="selectRange(0)"
            >
                Exact dates
            </button>

            <button
                class="small-btn plus-minus-btn"
                :class="{ selected: selectedRange === 1 }"
                @click="selectRange(1)"
            >
                <span class="plus-minus">
                    <span class="plus">+</span>
                    <span class="minus">-</span>
                </span>
                <span class="label">1 Day</span>
            </button>

            <button
                class="small-btn plus-minus-btn"
                :class="{ selected: selectedRange === 2 }"
                @click="selectRange(2)"
            >
                <span class="plus-minus">
                    <span class="plus">+</span>
                    <span class="minus">-</span>
                </span>
                <span class="label">2 Days</span>
            </button>

            <button
                class="small-btn plus-minus-btn"
                :class="{ selected: selectedRange === 3 }"
                @click="selectRange(3)"
            >
                <span class="plus-minus">
                    <span class="plus">+</span>
                    <span class="minus">-</span>
                </span>
                <span class="label">3 Days</span>
            </button>

            <button
                class="small-btn plus-minus-btn"
                :class="{ selected: selectedRange === 7 }"
                @click="selectRange(7)"
            >
                <span class="plus-minus">
                    <span class="plus">+</span>
                    <span class="minus">-</span>
                </span>
                <span class="label">7 Days</span>
            </button>
        </div>

        <div class="apply-button-container">
            <button class="clear-button" @click="clearDates">
                Clear Dates
            </button>
            <button class="apply-button md:hidden" @click="applyDates">
                Apply
            </button>
        </div>
    </div>
</template>
<script>
import isBefore from "date-fns/is_before";
import isAfter from "date-fns/is_after";
import format from "date-fns/format";
import { store, mutations } from "./store.js";

export default {
    name: "SearchCalendar",

    props: {
        dateOne: { type: [String, Date] },
        dateTwo: { type: [String, Date] },
        minDate: { type: [String, Date] },
        flexibleDate: { type: Number, default: 0 }
    },

    data() {
        let now = this.dateOne ? new Date(this.dateOne) : new Date();
        const utcDate = new Date(
            Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1)
        );
        return {
            currentUtcDate: utcDate,
            selectedDate1: this.dateOne
                ? new Date(this.dateOne).toISOString()
                : null,
            selectedDate2: this.dateTwo
                ? new Date(this.dateTwo).toISOString()
                : null,
            isSelectingDate1: true,
            weekdayNames: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
            selectedRange: this.flexibleDate,
            storeState: store.state,
            dateFormat: "YYYY-MM-DD",
            isMobile: window.innerWidth < 768,
            hoveredDate: null
        };
    },

    mounted() {
        this.selectedRange = this.storeState.flexibleNights;

        this.handleResize();
        if (this.isMobile) {
            document.body.style.overflowY = "hidden";
        }
        window.addEventListener("resize", this.handleResize);
    },

    beforeDestroy() {
        document.body.style.overflowY = "";
        window.removeEventListener("resize", this.handleResize);
    },

    methods: {
        handleResize() {
            this.isMobile = window.innerWidth < 768;
        },
        goToPreviousMonth() {
            const year = this.currentUtcDate.getUTCFullYear();
            const month = this.currentUtcDate.getUTCMonth() - 1;
            this.currentUtcDate = new Date(Date.UTC(year, month, 1));
        },
        goToNextMonth() {
            const year = this.currentUtcDate.getUTCFullYear();
            const month = this.currentUtcDate.getUTCMonth() + 1;
            this.currentUtcDate = new Date(Date.UTC(year, month, 1));
        },
        getMonthYearUTC(baseDate, offset) {
            const year = baseDate.getUTCFullYear();
            const month = baseDate.getUTCMonth() + offset;
            const date = new Date(Date.UTC(year, month, 1));
            return date.toLocaleString("en-US", {
                month: "long",
                year: "numeric",
                timeZone: "UTC"
            });
        },
        getDaysInMonthUTC(baseDate, offset) {
            const year = baseDate.getUTCFullYear();
            const month = baseDate.getUTCMonth() + offset;

            const start = new Date(Date.UTC(year, month, 1));
            const end = new Date(Date.UTC(year, month + 1, 0));
            const days = [];

            for (let day = 1; day <= end.getUTCDate(); day++) {
                const date = new Date(Date.UTC(year, month, day));
                days.push({
                    day: day,
                    iso: date.toISOString()
                });
            }

            return days;
        },

        isBeforeMinDate: function isBeforeMinDate(date) {
            if (!this.minDate) {
                return false;
            }

            return isBefore(date, this.minDate);
        },

        isSelectedDay(dateIso) {
            return (
                this.selectedDate1 === dateIso || this.selectedDate2 === dateIso
            );
        },

        getFirstDayOffset(baseDate, offset) {
            const year = baseDate.getUTCFullYear();
            const month = baseDate.getUTCMonth() + offset;
            const firstDay = new Date(Date.UTC(year, month, 1));
            const weekday = firstDay.getUTCDay();

            return (weekday + 6) % 7;
        },

        isInBetween(dateIso) {
            if (!this.selectedDate1 || !this.selectedDate2) return false;

            const date = new Date(dateIso);
            const start = new Date(this.selectedDate1);
            const end = new Date(this.selectedDate2);

            const utcDate = Date.UTC(
                date.getUTCFullYear(),
                date.getUTCMonth(),
                date.getUTCDate()
            );
            const utcStart = Date.UTC(
                start.getUTCFullYear(),
                start.getUTCMonth(),
                start.getUTCDate()
            );
            const utcEnd = Date.UTC(
                end.getUTCFullYear(),
                end.getUTCMonth(),
                end.getUTCDate()
            );

            const [minDate, maxDate] =
                utcStart <= utcEnd ? [utcStart, utcEnd] : [utcEnd, utcStart];

            return utcDate > minDate && utcDate < maxDate;
        },

        isInBetweenWithHover(dateIso) {
            if (!this.selectedDate1) return false;

            const start = new Date(this.selectedDate1);
            const end = this.selectedDate2
                ? new Date(this.selectedDate2)
                : this.hoveredDate
                ? new Date(this.hoveredDate)
                : null;

            if (!end) return false;

            const date = new Date(dateIso);

            const utcDate = Date.UTC(
                date.getUTCFullYear(),
                date.getUTCMonth(),
                date.getUTCDate()
            );
            const utcStart = Date.UTC(
                start.getUTCFullYear(),
                start.getUTCMonth(),
                start.getUTCDate()
            );
            const utcEnd = Date.UTC(
                end.getUTCFullYear(),
                end.getUTCMonth(),
                end.getUTCDate()
            );

            const [minDate, maxDate] =
                utcStart <= utcEnd ? [utcStart, utcEnd] : [utcEnd, utcStart];

            return utcDate > minDate && utcDate < maxDate;
        },

        selectDay(date) {
            if (this.isBeforeMinDate(date)) {
                return;
            }
            if (this.isSelectingDate1 || isBefore(date, this.selectedDate1)) {
                this.selectedDate1 = date;
                this.isSelectingDate1 = false;

                if (isBefore(this.selectedDate2, date)) {
                    this.selectedDate2 = null;
                }
            } else {
                this.selectedDate2 = date;
                this.isSelectingDate1 = true;

                if (isAfter(this.selectedDate1, date)) {
                    this.selectedDate1 = null;
                }
            }
        },
        applyDates() {
            if (this.storeState.destination.length) {
                this.$emit("load-search");
            }

            this.$emit("close-calendar");
        },
        clearDates() {
            this.selectedDate1 = null;
            this.selectedDate2 = null;
            this.isSelectingDate1 = true;
            this.$emit("cancelled");
        },
        selectRange(rangeValue) {
            this.selectedRange = rangeValue;
            this.$emit("range-selected", rangeValue);
        }
    },
    watch: {
        selectedDate1: function selectedDate1(newValue, oldValue) {
            const newDate =
                !newValue || newValue === ""
                    ? ""
                    : format(newValue, this.dateFormat);
            this.$emit("date-one-selected", newDate);
        },
        selectedDate2: function selectedDate2(newValue, oldValue) {
            const newDate =
                !newValue || newValue === ""
                    ? ""
                    : format(newValue, this.dateFormat);
            this.$emit("date-two-selected", newDate);
        },
        isMobile(newVal) {
            if (newVal) {
                document.body.style.overflowY = "hidden";
            } else {
                document.body.style.overflowY = "";
            }
        }
    }
};
</script>

<style scoped>
.calendar {
    font-family: Arial, sans-serif;
}

.nav-btn.top-left,
.nav-btn.top-right {
    position: absolute;
    top: 24px;
    z-index: 10;
    background-color: white;
    border: none;
    font-size: 30px;
    cursor: pointer;
    color: #548235;
    padding: 6px;
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.nav-btn.top-left {
    left: 15px;
}

.nav-btn.top-right {
    right: 15px;
}

.header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
}

.nav-btn {
    background-color: #fff;
    padding: 5px 10px;
    cursor: pointer;
    height: fit-content;
    font-size: 2rem;
    color: #548235;
    margin-top: -10px;
}

@media (max-width: 370px) {
    .nav-btn {
        padding: 0;
    }
}

.months {
    display: flex;
    gap: 40px;
    flex-grow: 1;
    justify-content: center;
}

.month {
    padding: 10px;
}

.month h3 {
    @apply text-xl;
    text-align: center;
    margin-bottom: 12px;
    font-weight: 600;
}

.days {
    display: flex;
    flex-wrap: wrap;
}

.day {
    width: 100%;
    height: 100%;
    text-align: center;
}

.days {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 0;
}

.day-header {
    text-align: center;
    padding: 4px 0;
}

.day-cell {
    width: 40px;
    height: 40px;
    display: flex;
    justify-content: center;
    align-items: center;
    border: 1px solid #e4e7e7;
}

.day-cell:hover {
    background-color: #f0f0f0;
}

.day-cell.empty {
    border: none;
    background: transparent;
}

.day-cell.selected {
    background-color: rgb(88, 158, 0);
}

.day-cell.selected .day {
    color: white;
}

.day-cell.in-between {
    background-color: rgb(129, 190, 50);
}

.day-cell.in-between .day {
    color: white;
}

.day-cell.in-between:hover {
    background-color: rgb(161, 211, 115);
}

.apply-button-container {
    display: flex;
    justify-content: space-between;
    padding: 10px;
}

.apply-button,
.clear-button {
    padding: 8px 16px;
    border: none;
    cursor: pointer;
    border-radius: 4px;
    font-weight: 500;
    transition: background-color 0.2s ease;
}

.apply-button {
    background-color: rgb(88, 158, 0);
    color: white;
}

.apply-button:hover {
    background-color: rgb(78, 140, 0);
}

.clear-button {
    background-color: #f2f2f2;
    color: #333;
}

.clear-button:hover {
    background-color: #dcdcdc;
}

.button-row {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 8px;
    margin: 15px 0;
    padding: 0 10px;
    box-sizing: border-box;
    max-width: 100%;
    overflow: hidden;
}

.small-btn {
    font-size: 12px;
    padding: 6px 10px;
    cursor: pointer;
    border-radius: 4px;
    border: 1px solid #888;
    background-color: white;
    flex: 1 1 45%;
    box-sizing: border-box;
    max-width: 100%;
    text-align: center;

    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
}

@media (min-width: 768px) {
    .small-btn {
        flex: 0 0 auto;
        min-width: 80px;
    }
}

@media (max-width: 767px) {
    .header {
        margin-top: 20px;
    }

    .nav-btn.top-left,
    .nav-btn.top-right {
        top: 84px;
    }

    .months {
        width: 100%;
    }

    .month {
        width: 100%;
    }

    .day-cell {
        width: 100%;
    }
}

.plus-minus-btn {
    font-weight: bold;
}

.plus-minus {
    display: flex;
    flex-direction: column;
    line-height: 12px;
    user-select: none;
    font-size: 14px;
    font-weight: bold;
    justify-content: center;
    align-items: center;
}

.plus {
    margin-bottom: -4px;
}

.minus {
    margin-top: -4px;
}

.label {
    font-weight: normal;
    font-size: 12px;
    user-select: none;
}

.small-btn.selected {
    background-color: #ddd;
    border: 2px solid black;
}

.close-btn {
    position: fixed;
    top: 5px;
    right: 5px;
    font-size: 36px;
    background: transparent;
    border: none;
    cursor: pointer;
    color: #548235;
    font-weight: bold;
    line-height: 1;
    z-index: 9999;
    user-select: none;
}

.close-btn:hover {
    color: #3d5e1a;
}

.close-btn-divider {
    width: 100vw;
    height: 1px;
    background-color: #e0e0e0;
    margin-top: 40px;
}

.day-cell.disabled {
    opacity: 0.3;
    pointer-events: none;
}

.day-cell.disabled .day {
    cursor: default;
}
</style>
